import { isAdvertisingConsentGranted } from './cookiePreferences';
import { isAuthActionUrlActive } from '../utils/authActionQuery.mjs';
const META_PIXEL_ID = String(import.meta.env.VITE_META_PIXEL_ID || '1475394377573862').trim();
const META_CAPI_ENDPOINT = String(import.meta.env.VITE_META_CAPI_ENDPOINT || '/api/meta-conversions').trim();
const DEFAULT_CURRENCY = String(import.meta.env.VITE_META_DEFAULT_CURRENCY || 'USD').trim().toUpperCase();
const IS_DEV = Boolean(import.meta.env.DEV);
const MAX_CONVERSIONS_FAILURES = 3;
const STATUS_EVENT = 'meta-tracking-status';

const STANDARD_EVENTS = new Set([
  'PageView',
  'ViewContent',
  'Search',
  'AddToCart',
  'InitiateCheckout',
  'AddPaymentInfo',
  'Purchase',
  'Lead',
  'CompleteRegistration',
  'Subscribe'
]);

const hasBrowserWindow = () => typeof window !== 'undefined';
const hasApiProxyInDev = () => {
  const useRemoteProxy = String(import.meta.env.VITE_USE_REMOTE_API_PROXY || '').toLowerCase() === 'true';
  return Boolean(
    import.meta.env.VITE_LOCAL_API_URL
    || import.meta.env.VITE_API_BASE_URL
    || useRemoteProxy
  );
};
const shouldSendConversionsApi = () => {
  if (!hasBrowserWindow() || !META_CAPI_ENDPOINT) return false;
  if (!IS_DEV) return true;
  return hasApiProxyInDev();
};
let conversionsApiSuppressed = false;
let conversionsApiFailureCount = 0;
let healthProbePromise = null;
let trackingStatus = {
  pixelIdConfigured: Boolean(META_PIXEL_ID),
  conversionsEndpoint: META_CAPI_ENDPOINT,
  canSendFromRuntime: shouldSendConversionsApi(),
  suppressed: false,
  suppressionReason: '',
  failureCount: 0,
  successCount: 0,
  lastAttemptAt: '',
  lastSuccessAt: '',
  lastProbeAt: '',
  lastResponseStatus: null,
  lastError: '',
  healthCheckOk: null
};

const publishTrackingStatus = () => {
  if (!hasBrowserWindow()) return;
  const snapshot = {
    ...trackingStatus,
    canSendFromRuntime: shouldSendConversionsApi(),
    suppressed: Boolean(conversionsApiSuppressed)
  };
  window.__stocktagMetaTrackingStatus = snapshot;
  window.dispatchEvent(new CustomEvent(STATUS_EVENT, { detail: snapshot }));
};

const patchTrackingStatus = (patch) => {
  trackingStatus = {
    ...trackingStatus,
    ...patch,
    canSendFromRuntime: shouldSendConversionsApi(),
    suppressed: Boolean(conversionsApiSuppressed)
  };
  publishTrackingStatus();
};

const suppressConversionsApi = (reason) => {
  conversionsApiSuppressed = true;
  patchTrackingStatus({
    suppressionReason: String(reason || 'suppressed'),
    lastError: String(reason || 'suppressed')
  });
};

const markConversionsFailure = (reason, statusCode = null) => {
  conversionsApiFailureCount += 1;
  const shouldSuppressImmediately = [400, 401, 403, 404, 405, 501].includes(Number(statusCode));
  if (shouldSuppressImmediately || conversionsApiFailureCount >= MAX_CONVERSIONS_FAILURES) {
    suppressConversionsApi(reason || `http_${statusCode || 'error'}`);
  } else {
    patchTrackingStatus({
      suppressionReason: '',
      lastError: String(reason || 'request_failed')
    });
  }

  patchTrackingStatus({
    failureCount: conversionsApiFailureCount,
    lastResponseStatus: statusCode
  });
};

const readCookie = (key) => {
  if (!hasBrowserWindow()) return '';
  try {
    const escapedKey = String(key || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const match = document.cookie.match(new RegExp(`(?:^|; )${escapedKey}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : '';
  } catch {
    return '';
  }
};

const isConsentGranted = () => isAdvertisingConsentGranted();

const toInteger = (value, fallback = 0) => {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const toNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const compactObject = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  return Object.entries(value).reduce((acc, [key, entry]) => {
    if (entry === undefined || entry === null || entry === '') return acc;
    acc[key] = entry;
    return acc;
  }, {});
};

const normalizeCurrency = (value) => {
  const normalized = String(value || DEFAULT_CURRENCY || 'USD').trim().toUpperCase();
  return normalized || 'USD';
};

const splitDisplayName = (displayName = '') => {
  const normalized = String(displayName || '').trim().replace(/\s+/g, ' ');
  if (!normalized) return { firstName: '', lastName: '' };
  const [firstName, ...rest] = normalized.split(' ');
  return {
    firstName: firstName || '',
    lastName: rest.join(' ').trim()
  };
};

const buildUserData = (user = {}) => {
  const safeUser = user || {};
  const { firstName, lastName } = splitDisplayName(safeUser.displayName || safeUser.name || '');

  return compactObject({
    email: safeUser.email || safeUser.userEmail || '',
    phone: safeUser.phone || safeUser.phoneNumber || '',
    external_id: safeUser.externalId || safeUser.uid || safeUser.userId || '',
    first_name: safeUser.firstName || firstName,
    last_name: safeUser.lastName || lastName,
    fbp: safeUser.fbp || readCookie('_fbp'),
    fbc: safeUser.fbc || readCookie('_fbc')
  });
};

const getEventSourceUrl = (path) => {
  if (!hasBrowserWindow()) return '';
  try {
    if (!path) return window.location.href;
    return new URL(path, window.location.origin).toString();
  } catch {
    return window.location.href;
  }
};

export const createMetaEventId = (eventName = 'event') => {
  const safePrefix = String(eventName || 'event').replace(/[^a-zA-Z0-9_-]/g, '').slice(0, 24) || 'event';
  if (hasBrowserWindow() && window.crypto?.randomUUID) {
    return `${safePrefix}_${window.crypto.randomUUID()}`;
  }
  return `${safePrefix}_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
};

const getHealthCheckUrl = () => {
  if (!META_CAPI_ENDPOINT) return '';
  return `${META_CAPI_ENDPOINT}${META_CAPI_ENDPOINT.includes('?') ? '&' : '?'}health=1`;
};

export const probeMetaConversionsApi = async () => {
  if (!shouldSendConversionsApi() || conversionsApiSuppressed || !META_CAPI_ENDPOINT) {
    return { ...trackingStatus, canSendFromRuntime: shouldSendConversionsApi(), suppressed: conversionsApiSuppressed };
  }

  if (healthProbePromise) return healthProbePromise;

  const healthUrl = getHealthCheckUrl();
  healthProbePromise = (async () => {
    patchTrackingStatus({
      lastProbeAt: new Date().toISOString()
    });

    try {
      const response = await fetch(healthUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json().catch(() => ({}));
      patchTrackingStatus({
        healthCheckOk: response.ok,
        lastResponseStatus: response.status
      });

      if (!response.ok) {
        markConversionsFailure(`health_${response.status}`, response.status);
      } else if (result && result.configured === false) {
        patchTrackingStatus({
          suppressionReason: '',
          lastError: 'meta_not_configured'
        });
      }
    } catch {
      markConversionsFailure('health_network_error');
    } finally {
      healthProbePromise = null;
    }

    return { ...trackingStatus, canSendFromRuntime: shouldSendConversionsApi(), suppressed: conversionsApiSuppressed };
  })();

  return healthProbePromise;
};

const sendToConversionsApi = async (payload) => {
  if (!shouldSendConversionsApi() || conversionsApiSuppressed) return;
  patchTrackingStatus({
    lastAttemptAt: new Date().toISOString(),
    suppressionReason: '',
    lastError: ''
  });

  try {
    const response = await fetch(META_CAPI_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload),
      keepalive: true
    });

    patchTrackingStatus({
      lastResponseStatus: response.status,
      healthCheckOk: response.ok
    });

    if (!response.ok) {
      markConversionsFailure(`http_${response.status}`, response.status);
      return;
    }

    conversionsApiFailureCount = 0;
    patchTrackingStatus({
      failureCount: 0,
      successCount: Number(trackingStatus.successCount || 0) + 1,
      lastSuccessAt: new Date().toISOString(),
      suppressionReason: '',
      lastError: ''
    });
  } catch (error) {
    markConversionsFailure(error?.name === 'AbortError' ? 'request_timeout' : 'network_error');
  }
};

export const getMetaTrackingStatus = () => ({
  ...trackingStatus,
  canSendFromRuntime: shouldSendConversionsApi(),
  suppressed: Boolean(conversionsApiSuppressed)
});

export const META_TRACKING_STATUS_EVENT = STATUS_EVENT;

if (hasBrowserWindow()) {
  publishTrackingStatus();
}

export const trackMetaEvent = (eventName, customData = {}, options = {}) => {
  if (!isConsentGranted()) return null;
  // Firebase auth-action URLs carry a one-time oobCode in the query string.
  if (isAuthActionUrlActive()) return null;

  const safeEventName = String(eventName || '').trim();
  if (!safeEventName) return null;

  const {
    forceCustomEvent = false,
    sendToServer = true,
    eventId,
    eventSourceUrl,
    actionSource = 'website',
    userData = {}
  } = options;

  const safeEventId = eventId || createMetaEventId(safeEventName);
  const safeCustomData = compactObject(customData);
  const safeUserData = buildUserData(userData);
  const safeEventSourceUrl = eventSourceUrl || getEventSourceUrl();

  if (hasBrowserWindow() && META_PIXEL_ID && typeof window.fbq === 'function') {
    const trackingMethod = !forceCustomEvent && STANDARD_EVENTS.has(safeEventName) ? 'track' : 'trackCustom';
    try {
      window.fbq(trackingMethod, safeEventName, safeCustomData, { eventID: safeEventId });
    } catch {
      // no-op on tracking errors
    }
  }

  if (sendToServer) {
    void sendToConversionsApi({
      event_name: safeEventName,
      event_time: Math.floor(Date.now() / 1000),
      event_id: safeEventId,
      event_source_url: safeEventSourceUrl,
      action_source: actionSource,
      custom_data: safeCustomData,
      user_data: safeUserData
    });
  }

  return safeEventId;
};

export const trackPageView = ({ path = '', user = null, sendToServer = true } = {}) =>
  trackMetaEvent('PageView', {}, {
    eventSourceUrl: getEventSourceUrl(path),
    userData: user,
    sendToServer
  });

export const trackPricingView = ({ source = 'pricing', user = null } = {}) =>
  trackMetaEvent('ViewContent', {
    content_name: 'Pricing',
    content_category: 'pricing',
    source
  }, {
    userData: user
  });

export const trackCompleteRegistration = ({ method = 'email', user = null } = {}) =>
  trackMetaEvent('CompleteRegistration', {
    status: true,
    registration_method: String(method || 'email')
  }, {
    userData: user
  });

export const trackInitiateCheckout = ({
  packageId = '',
  packageName = '',
  packageType = '',
  billingPeriod = '',
  price = 0,
  quantity = 1,
  currency = DEFAULT_CURRENCY,
  user = null
} = {}) => {
  const safeQuantity = Math.max(1, toInteger(quantity, 1));
  const unitPrice = Math.max(0, toNumber(price, 0));
  const totalValue = Number((unitPrice * safeQuantity).toFixed(2));
  const safePackageId = String(packageId || packageName || 'offer');
  const safeCurrency = normalizeCurrency(currency);

  return trackMetaEvent('InitiateCheckout', {
    currency: safeCurrency,
    value: totalValue,
    content_name: packageName || safePackageId,
    content_type: 'product',
    content_ids: [safePackageId],
    contents: [{
      id: safePackageId,
      quantity: safeQuantity,
      item_price: unitPrice
    }],
    num_items: safeQuantity,
    package_type: packageType || undefined,
    billing_period: billingPeriod || undefined
  }, {
    userData: user
  });
};

export const trackPurchase = ({
  transactionId = '',
  packageId = '',
  packageName = '',
  packageType = '',
  billingPeriod = '',
  price = 0,
  value,
  quantity = 1,
  credits,
  currency = DEFAULT_CURRENCY,
  user = null,
  eventId
} = {}) => {
  const safeQuantity = Math.max(1, toInteger(quantity, 1));
  const unitPrice = Math.max(0, toNumber(price, 0));
  const fallbackValue = unitPrice * safeQuantity;
  const safeValue = Math.max(0, toNumber(value, fallbackValue));
  const safeCurrency = normalizeCurrency(currency);
  const safePackageId = String(packageId || packageName || transactionId || 'purchase');

  return trackMetaEvent('Purchase', {
    currency: safeCurrency,
    value: Number(safeValue.toFixed(2)),
    transaction_id: transactionId || undefined,
    content_name: packageName || safePackageId,
    content_type: 'product',
    content_ids: [safePackageId],
    contents: [{
      id: safePackageId,
      quantity: safeQuantity,
      item_price: unitPrice
    }],
    num_items: safeQuantity,
    package_type: packageType || undefined,
    billing_period: billingPeriod || undefined,
    credits: Number.isFinite(Number(credits)) ? Number(credits) : undefined
  }, {
    userData: user,
    // A deterministic id (e.g. purchase_<transactionId>) lets Meta dedupe the
    // pixel and Conversions API copies of the same purchase.
    eventId: eventId || undefined
  });
};

export const trackSubscribe = ({
  packageId = '',
  packageName = '',
  packageType = '',
  billingPeriod = '',
  price = 0,
  currency = DEFAULT_CURRENCY,
  user = null
} = {}) => {
  const safePrice = Math.max(0, toNumber(price, 0));
  const safeCurrency = normalizeCurrency(currency);
  const safePackageName = packageName || packageId || 'subscription';

  return trackMetaEvent('Subscribe', {
    currency: safeCurrency,
    value: Number(safePrice.toFixed(2)),
    content_name: safePackageName,
    content_category: packageType || 'subscription',
    billing_period: billingPeriod || undefined
  }, {
    userData: user
  });
};
